import { formatMoney } from '@theme/money-formatting';

/**
 * Free shipping progress bar.
 *
 * The threshold is set in the store's currency, so it's converted to the cart's currency here
 * using Shopify's exchange rate (`window.Shopify.currency.rate`), which Liquid doesn't expose.
 *
 * Cart updates morph this element's attributes but skip its children (`data-skip-subtree-update`),
 * so the bar is re-rendered whenever `data-cart-total` changes.
 */
class FreeShippingBar extends HTMLElement {
  static observedAttributes = ['data-cart-total'];

  connectedCallback() {
    this.#render();
  }

  attributeChangedCallback() {
    // Let the morph finish updating the remaining attributes first
    queueMicrotask(() => this.#render());
  }

  #render() {
    const status = this.querySelector('.free-shipping-bar');
    const message = this.querySelector('.free-shipping-bar__message');
    const fill = this.querySelector('.free-shipping-bar__fill');
    if (!(status instanceof HTMLElement) || !message || !(fill instanceof HTMLElement)) return;

    const { thresholdCents, cartTotal, currency, moneyFormat, remainingText, unlockedText } = this.dataset;
    const rate = Number(window.Shopify?.currency?.rate ?? 1);
    const threshold = Math.round(Number(thresholdCents) * rate);
    const total = Number(cartTotal);

    if (!threshold || !rate || Number.isNaN(total)) {
      status.hidden = true;
      return;
    }

    const remaining = threshold - total;
    const progress = Math.min(100, (total / threshold) * 100);

    message.textContent =
      remaining > 0
        ? (remainingText ?? '').replace('[amount]', formatMoney(remaining, moneyFormat ?? '{{amount}}', currency ?? ''))
        : unlockedText ?? '';
    status.classList.toggle('free-shipping-bar--unlocked', remaining <= 0);
    fill.style.setProperty('--free-shipping-progress', `${progress}%`);
    status.hidden = false;
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
